import * as React from 'react'
import { Check, Loader2, Plus } from 'lucide-react'
import { toast } from 'sonner'
import {
  useCreateIngestIntakeMutation,
  useListIngestMappingConfigsQuery,
  usePatchIngestIntakeMutation,
  type CreateIngestIntakeBody,
  type IngestIntake,
  type IngestIntakeTabularTarget,
  type IngestIntakeTarget,
  type IngestIntakeTargetKind,
  type IngestIntakeUnstructuredTarget,
  type PatchIngestIntakeBody
} from '@axilon-apps/domains-ontology-ui-state'

import { readMutationError } from '../../../components/explorer'
import { Button } from '../../../components/ui/button'
import { Checkbox } from '../../../components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '../../../components/ui/dialog'
import { Input } from '../../../components/ui/input'
import { Label } from '../../../components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../../../components/ui/select'
import { Switch } from '../../../components/ui/switch'
import { Textarea } from '../../../components/ui/textarea'
import { ToggleGroup, ToggleGroupItem } from '../../../components/ui/toggle-group'
import { cn } from '../../../lib/utils'
import type { LandingSite } from '../wizard/IngestLanding'
import { siteOptionLabel } from '../wizard/siteIds'
import {
  ACCEPT_PRESETS,
  acceptForForm,
  acceptForRequest,
  normalizeAccept,
  siteUuid
} from './intake-view'

type TargetChoice = 'none' | IngestIntakeTargetKind

const DEFAULT_ACCEPT = ['xlsx', 'xlsm', 'xls', 'csv']

const TARGET_OPTIONS: ReadonlyArray<[TargetChoice, string]> = [
  ['none', 'None yet'],
  ['mapped_tabular_ingest', 'Tabular config'],
  ['unstructured', 'Unstructured']
]

const CONTROL_CLASS = 'h-8 text-xs'

interface IntakeDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  sites: LandingSite[]
  intake?: IngestIntake | null
}

export function IntakeDialog({ open, onOpenChange, sites, intake }: IntakeDialogProps) {
  const editing = Boolean(intake)
  const [createIntake, createState] = useCreateIngestIntakeMutation()
  const [patchIntake, patchState] = usePatchIngestIntakeMutation()
  const configsQuery = useListIngestMappingConfigsQuery(
    { kind: 'mapped_tabular_ingest', latest: true, limit: 500 },
    { skip: !open }
  )
  const [name, setName] = React.useState('')
  const [selectedSite, setSelectedSite] = React.useState('')
  const [description, setDescription] = React.useState('')
  const [accept, setAccept] = React.useState<string[]>(DEFAULT_ACCEPT)
  const [branchName, setBranchName] = React.useState('')
  const [branchTouched, setBranchTouched] = React.useState(false)
  const [targetChoice, setTargetChoice] = React.useState<TargetChoice>('none')
  const [configName, setConfigName] = React.useState('')
  const [pinVersion, setPinVersion] = React.useState(false)
  const [configVersion, setConfigVersion] = React.useState('')
  const [unstructuredBranchId, setUnstructuredBranchId] = React.useState('')
  const [additionalContext, setAdditionalContext] = React.useState('')
  const [enabled, setEnabled] = React.useState(true)
  const [fieldErrors, setFieldErrors] = React.useState<Record<string, string>>({})
  const [formError, setFormError] = React.useState('')

  React.useEffect(() => {
    if (!open) return
    const intakeSite =
      sites.find((site) => siteUuid(site.id) === intake?.siteId)?.id ?? intake?.siteId ?? ''
    const tabular =
      intake?.targetKind === 'mapped_tabular_ingest'
        ? (intake.target as IngestIntakeTabularTarget | null)
        : null
    const unstructured =
      intake?.targetKind === 'unstructured'
        ? (intake.target as IngestIntakeUnstructuredTarget | null)
        : null
    setName(intake?.name ?? '')
    setSelectedSite(intakeSite)
    setDescription(intake?.description ?? '')
    setAccept(intake ? acceptForForm(intake.accept) : DEFAULT_ACCEPT)
    setBranchName(intake?.branchName ?? '')
    setBranchTouched(Boolean(intake))
    setTargetChoice(intake?.targetKind ?? 'none')
    setConfigName(tabular?.configName ?? '')
    setPinVersion(tabular?.configVersion !== undefined)
    setConfigVersion(tabular?.configVersion ? String(tabular.configVersion) : '')
    setUnstructuredBranchId(unstructured?.branchId ?? '')
    setAdditionalContext(unstructured?.additionalContext ?? '')
    setEnabled(intake?.enabled ?? true)
    setFieldErrors({})
    setFormError('')
  }, [intake, open, sites])

  const busy = createState.isLoading || patchState.isLoading
  const selectedSiteUuid = siteUuid(selectedSite)

  const clearField = (field: string) => {
    setFieldErrors((current) => {
      if (!(field in current)) return current
      const next = { ...current }
      delete next[field]
      return next
    })
    setFormError('')
  }

  const updateName = (value: string) => {
    setName(value)
    if (!branchTouched) setBranchName(defaultBranchName(value))
    clearField('name')
  }

  const validate = (): boolean => {
    const errors: Record<string, string> = {}
    if (!editing && !/^[a-z0-9-]{3,64}$/.test(name.trim())) {
      errors.name = 'Use 3–64 lowercase letters, digits, or dashes.'
    }
    if (!selectedSiteUuid) errors.siteId = 'Select a site whose identifier is a UUID.'
    if (accept.length < 1) errors.accept = 'Choose at least one file type.'
    if (branchName.trim() === 'main' || !/^[a-z0-9][a-z0-9-]{2,63}$/.test(branchName.trim())) {
      errors.branchName = 'Use 3–64 lowercase letters, digits, or dashes; not main.'
    }
    if (targetChoice === 'mapped_tabular_ingest' && !configName) {
      errors.target = 'Select a tabular config.'
    }
    if (targetChoice === 'mapped_tabular_ingest' && pinVersion) {
      const version = Number(configVersion)
      if (!Number.isInteger(version) || version < 1) errors.target = 'Enter a valid config version.'
    }
    if (targetChoice === 'unstructured' && !selectedSiteUuid) {
      errors.target = 'Unstructured ingest requires a site.'
    }
    setFieldErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!validate() || !selectedSiteUuid || busy) return
    setFormError('')
    try {
      const target = buildTarget({
        choice: targetChoice,
        siteId: selectedSiteUuid,
        configName,
        pinVersion,
        configVersion,
        unstructuredBranchId,
        additionalContext
      })
      if (intake) {
        const body = patchBody(intake, {
          description: description.trim(),
          enabled,
          accept: acceptForRequest(accept),
          branchName: branchName.trim(),
          targetKind: targetChoice === 'none' ? null : targetChoice,
          target
        })
        await patchIntake({ id: intake.id, body }).unwrap()
        toast.success(`Updated ${intake.name}`)
      } else {
        const body: CreateIngestIntakeBody = {
          siteId: selectedSiteUuid,
          name: name.trim(),
          description: description.trim(),
          accept: acceptForRequest(accept),
          branchName: branchName.trim(),
          targetKind: targetChoice === 'none' ? null : targetChoice,
          target
        }
        const created = await createIntake(body).unwrap()
        if (!enabled) {
          await patchIntake({ id: created.id, body: { enabled: false } }).unwrap()
        }
        toast.success(`Created ${body.name}`)
      }
      onOpenChange(false)
    } catch (error) {
      const parsed = mutationErrors(error)
      setFieldErrors(parsed.fields)
      setFormError(
        parsed.form || (Object.keys(parsed.fields).length === 0 ? readMutationError(error) : '')
      )
    }
  }

  const selectedConfig = (configsQuery.data ?? []).find((config) => config.name === configName)
  const intakeSiteLabel =
    sites.find((site) => site.id === selectedSite)?.name ?? intake?.siteId ?? 'Select a site'

  return (
    <Dialog open={open} onOpenChange={(next) => !busy && onOpenChange(next)}>
      <DialogContent className="max-h-[90vh] gap-3 overflow-y-auto p-5 sm:max-w-xl">
        <DialogHeader className="gap-1">
          <DialogTitle className="text-sm">
            {editing ? `Edit ${intake?.name}` : 'New intake'}
          </DialogTitle>
          <DialogDescription className="text-11">
            A stable address other systems push files to, and how those files are ingested.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col gap-3.5">
          <div className="grid gap-3 sm:grid-cols-2">
            <Field
              label="Name"
              hint="Lowercase letters, digits, dashes. Becomes the sender's URL."
              error={fieldErrors.name}
            >
              <Input
                value={name}
                onChange={(event) => updateName(event.target.value)}
                disabled={editing || busy}
                placeholder="kanban-daily"
                className={cn(CONTROL_CLASS, 'font-mono')}
                aria-label="Intake name"
              />
            </Field>
            <Field
              label="Site"
              hint={selectedSiteUuid ?? 'Pick a site to resolve its UUID.'}
              hintMono={Boolean(selectedSiteUuid)}
              error={fieldErrors.siteId}
            >
              {editing ? (
                <div
                  className={cn(
                    CONTROL_CLASS,
                    'flex items-center truncate rounded-md border border-input bg-bg-sunk/40 px-2.5 text-ink-2'
                  )}
                >
                  {intakeSiteLabel}
                </div>
              ) : (
                <Select
                  value={selectedSite}
                  onValueChange={(value) => {
                    setSelectedSite(value)
                    clearField('siteId')
                  }}
                >
                  <SelectTrigger size="sm" className="w-full text-xs" aria-label="Intake site">
                    <SelectValue placeholder="Select a site" />
                  </SelectTrigger>
                  <SelectContent>
                    {sites.map((site) => (
                      <SelectItem key={site.id} value={site.id}>
                        {siteOptionLabel(site.name, site.id)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </Field>
          </div>

          <Field label="Description" optional error={fieldErrors.description}>
            <Textarea
              aria-label="Intake description"
              value={description}
              onChange={(event) => {
                setDescription(event.target.value)
                clearField('description')
              }}
              maxLength={1000}
              rows={2}
              placeholder="What sends files to this intake?"
              className="min-h-0 text-xs"
            />
          </Field>

          <Field label="Accepted file types" error={fieldErrors.accept}>
            <AcceptPicker
              value={accept}
              disabled={busy}
              onChange={(next) => {
                setAccept(next)
                clearField('accept')
              }}
            />
          </Field>

          <Field
            label="Branch"
            hint="Reserved for this intake; nobody else can create a branch with this name."
            error={fieldErrors.branchName}
          >
            <Input
              value={branchName}
              onChange={(event) => {
                setBranchTouched(true)
                setBranchName(event.target.value)
                clearField('branchName')
              }}
              className={cn(CONTROL_CLASS, 'font-mono')}
              aria-label="Intake branch name"
            />
            {editing && intake?.branch ? (
              <p className="text-11 text-st-warn">
                The current active branch keeps its unmerged work under the old name.
              </p>
            ) : null}
          </Field>

          <Field label="Target" error={fieldErrors.target}>
            <ToggleGroup
              type="single"
              variant="outline"
              size="sm"
              value={targetChoice}
              onValueChange={(value) => {
                // A single-select toggle group reports '' when the active item
                // is clicked again; the target always has exactly one choice.
                if (!value) return
                setTargetChoice(value as TargetChoice)
                clearField('target')
              }}
              aria-label="Intake target"
            >
              {TARGET_OPTIONS.map(([value, label]) => (
                <ToggleGroupItem
                  key={value}
                  value={value}
                  className="h-7 px-2.5 text-11 font-medium"
                >
                  {label}
                </ToggleGroupItem>
              ))}
            </ToggleGroup>

            {targetChoice === 'none' ? (
              <p className="text-11 text-ink-3">
                Files park as pending until a target is set. You can run them by hand from the
                intake&apos;s received-files list.
              </p>
            ) : null}

            {targetChoice === 'mapped_tabular_ingest' ? (
              <div className="grid gap-2.5 rounded-md border border-line bg-bg-sunk/40 p-3">
                <div className="grid gap-1">
                  <Label htmlFor="intake-config" className="text-11 text-ink-2">
                    Config
                  </Label>
                  <Select
                    value={configName}
                    onValueChange={(value) => {
                      const config = (configsQuery.data ?? []).find(
                        (candidate) => candidate.name === value
                      )
                      setConfigName(value)
                      if (config) setConfigVersion(String(config.version))
                      clearField('target')
                    }}
                  >
                    <SelectTrigger id="intake-config" size="sm" className="w-full text-xs">
                      <SelectValue
                        placeholder={
                          configsQuery.isLoading ? 'Loading configs…' : 'Select a config'
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {(configsQuery.data ?? []).map((config) => (
                        <SelectItem key={config.id} value={config.name}>
                          {config.name} · v{config.version}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <Label className="cursor-pointer gap-2 text-11 font-normal text-ink">
                    <Checkbox
                      checked={pinVersion}
                      onCheckedChange={(checked) => setPinVersion(checked === true)}
                    />
                    Pin to version
                  </Label>
                  <Input
                    id="intake-config-version"
                    aria-label="Config version"
                    type="number"
                    min={1}
                    value={configVersion || selectedConfig?.version || ''}
                    disabled={!pinVersion}
                    onChange={(event) => setConfigVersion(event.target.value)}
                    className="h-7 w-20 text-xs"
                  />
                  <span className="text-11 text-ink-3">
                    {selectedConfig
                      ? pinVersion
                        ? `Latest is v${selectedConfig.version}.`
                        : `Follows the latest version (v${selectedConfig.version}).`
                      : 'Unpinned intakes follow the latest saved version.'}
                  </span>
                </div>
                {configsQuery.isError ? (
                  <p role="alert" className="text-11 text-st-down">
                    Could not load tabular configs.
                  </p>
                ) : null}
              </div>
            ) : null}

            {targetChoice === 'unstructured' ? (
              <div className="grid gap-2.5 rounded-md border border-line bg-bg-sunk/40 p-3 sm:grid-cols-2">
                <p className="text-11 text-ink-3 sm:col-span-2">
                  Ingests into the intake site{' '}
                  <span className="font-mono text-ink">{selectedSiteUuid ?? '—'}</span>.
                </p>
                <Field label="Branch ID" optional>
                  <Input
                    aria-label="Unstructured branch ID"
                    value={unstructuredBranchId}
                    onChange={(event) => setUnstructuredBranchId(event.target.value)}
                    className={cn(CONTROL_CLASS, 'font-mono')}
                  />
                </Field>
                <Field label="Additional context" optional>
                  <Textarea
                    aria-label="Unstructured additional context"
                    value={additionalContext}
                    onChange={(event) => setAdditionalContext(event.target.value)}
                    rows={2}
                    className="min-h-0 text-xs"
                  />
                </Field>
              </div>
            ) : null}
          </Field>

          <Label
            htmlFor="intake-enabled"
            className="cursor-pointer gap-2.5 text-11 font-normal text-ink"
          >
            <Switch id="intake-enabled" checked={enabled} onCheckedChange={setEnabled} />
            <span className="font-medium">Enabled</span>
            <span className="text-ink-3">Disabled intakes reject new file pushes.</span>
          </Label>

          {formError ? (
            <p role="alert" className="rounded-md bg-st-down-bg px-3 py-2 text-11 text-st-down">
              {formError}
            </p>
          ) : null}

          <DialogFooter>
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-7 text-xs"
              disabled={busy}
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" size="sm" className="h-7 text-xs" disabled={busy}>
              {busy ? <Loader2 className="size-3.5 animate-spin" /> : null}
              {editing ? 'Save changes' : 'Create intake'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

/**
 * Toggle pills for the accepted extensions. Presets come first; anything
 * already on the intake that is not a preset is shown too, so it can be
 * deselected. "Other" reveals a one-shot input for an extension not listed.
 */
function AcceptPicker({
  value,
  disabled,
  onChange
}: {
  value: string[]
  disabled?: boolean
  onChange: (next: string[]) => void
}) {
  const [adding, setAdding] = React.useState(false)
  const [draft, setDraft] = React.useState('')
  const options = React.useMemo(
    () => Array.from(new Set<string>([...ACCEPT_PRESETS, ...value])),
    [value]
  )

  const toggle = (extension: string) => {
    onChange(
      value.includes(extension)
        ? value.filter((candidate) => candidate !== extension)
        : normalizeAccept([...value, extension])
    )
  }

  const commitDraft = () => {
    const next = normalizeAccept([...value, ...draft.split(/[\s,]+/)])
    if (next.length !== value.length) onChange(next)
    setDraft('')
    setAdding(false)
  }

  return (
    <div
      className="flex flex-wrap items-center gap-1.5"
      role="group"
      aria-label="Accepted file types"
    >
      {options.map((extension) => {
        const selected = value.includes(extension)
        return (
          <button
            key={extension}
            type="button"
            aria-pressed={selected}
            disabled={disabled}
            onClick={() => toggle(extension)}
            className={cn(
              'inline-flex h-7 items-center gap-1 rounded-full border px-2.5 font-mono text-11 font-medium transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/30 disabled:pointer-events-none disabled:opacity-50',
              selected
                ? 'border-brand/40 bg-brand-soft text-brand'
                : 'border-line text-ink-3 hover:bg-bg-sunk hover:text-ink'
            )}
          >
            {selected ? <Check className="size-3" aria-hidden /> : null}
            {extension}
          </button>
        )
      })}
      {adding ? (
        <Input
          autoFocus
          aria-label="Other file type"
          value={draft}
          placeholder="ext"
          disabled={disabled}
          onChange={(event) => setDraft(event.target.value)}
          onBlur={commitDraft}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault()
              commitDraft()
            } else if (event.key === 'Escape') {
              event.preventDefault()
              setDraft('')
              setAdding(false)
            }
          }}
          className="h-7 w-20 rounded-full px-2.5 font-mono text-11"
        />
      ) : (
        <button
          type="button"
          disabled={disabled}
          onClick={() => setAdding(true)}
          className="inline-flex h-7 items-center gap-1 rounded-full border border-dashed border-line px-2.5 text-11 text-ink-3 transition-colors hover:bg-bg-sunk hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/30 disabled:pointer-events-none disabled:opacity-50"
        >
          <Plus className="size-3" aria-hidden />
          Other
        </button>
      )}
    </div>
  )
}

function Field({
  label,
  hint,
  hintMono = false,
  optional = false,
  error,
  children
}: {
  label: string
  hint?: string
  hintMono?: boolean
  optional?: boolean
  error?: string
  children: React.ReactNode
}) {
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <Label className="text-11 text-ink-2">
        {label}
        {optional ? <span className="font-normal text-ink-3">optional</span> : null}
      </Label>
      {children}
      {error ? (
        <p role="alert" className="text-11 text-st-down">
          {error}
        </p>
      ) : hint ? (
        <p className={cn('truncate text-11 text-ink-3', hintMono && 'font-mono')} title={hint}>
          {hint}
        </p>
      ) : null}
    </div>
  )
}

function defaultBranchName(name: string): string {
  return `intake-${name.trim()}`.slice(0, 64)
}

function buildTarget(input: {
  choice: TargetChoice
  siteId: string
  configName: string
  pinVersion: boolean
  configVersion: string
  unstructuredBranchId: string
  additionalContext: string
}): IngestIntakeTarget | null {
  if (input.choice === 'none') return null
  if (input.choice === 'mapped_tabular_ingest') {
    return {
      configName: input.configName,
      ...(input.pinVersion ? { configVersion: Number(input.configVersion) } : {})
    }
  }
  return {
    siteId: input.siteId,
    ...(input.unstructuredBranchId.trim() ? { branchId: input.unstructuredBranchId.trim() } : {}),
    ...(input.additionalContext.trim() ? { additionalContext: input.additionalContext.trim() } : {})
  }
}

function patchBody(
  current: IngestIntake,
  next: Required<Pick<PatchIngestIntakeBody, 'description' | 'enabled' | 'accept' | 'branchName'>> &
    Pick<PatchIngestIntakeBody, 'targetKind' | 'target'>
): PatchIngestIntakeBody {
  const body: PatchIngestIntakeBody = {}
  if (next.description !== current.description) body.description = next.description
  if (next.enabled !== current.enabled) body.enabled = next.enabled
  if (JSON.stringify(next.accept) !== JSON.stringify(current.accept)) body.accept = next.accept
  if (next.branchName !== current.branchName) body.branchName = next.branchName
  if (
    next.targetKind !== current.targetKind ||
    JSON.stringify(next.target) !== JSON.stringify(current.target)
  ) {
    body.targetKind = next.targetKind
    body.target = next.target
  }
  return body
}

function mutationErrors(error: unknown): {
  fields: Record<string, string>
  form: string
} {
  if (!error || typeof error !== 'object') return { fields: {}, form: '' }
  const payload = error as { data?: unknown }
  if (!payload.data || typeof payload.data !== 'object') return { fields: {}, form: '' }
  const data = payload.data as {
    reason?: unknown
    message?: unknown
    details?: unknown
  }
  const details =
    data.details && typeof data.details === 'object'
      ? Object.fromEntries(
          Object.entries(data.details).filter(
            (entry): entry is [string, string] => typeof entry[1] === 'string'
          )
        )
      : {}
  if (data.reason === 'INTAKE_NAME_TAKEN') {
    details.name = 'An intake with this name already exists.'
  }
  if (data.reason === 'INTAKE_BRANCH_NAME_TAKEN' || data.reason === 'BRANCH_NAME_IN_USE') {
    details.branchName =
      typeof data.message === 'string' ? data.message : 'This branch name is already in use.'
  }
  const nestedTargetError = Object.entries(details).find(([field]) =>
    field.startsWith('target.')
  )?.[1]
  if (!details.target && nestedTargetError) details.target = nestedTargetError
  return {
    fields: details,
    form: Object.keys(details).length === 0 && typeof data.message === 'string' ? data.message : ''
  }
}
